import { useExplore } from '@/stores/explore';
import { LineChart } from '@mui/x-charts';
import { Button, cn, toast } from '@skand/ui';
import { Cartographic, toCartesian, toDegrees } from '@skand/viewer-component-v2';
import { useMemo } from 'react';
import { getMeasurementDifferences } from '../AnnotationDetails/utils';

interface SectioningViewProps {
  sections: Cartographic[][];
}

export const SectioningView = ({ sections }: SectioningViewProps) => {
  const annotationOriginal = useExplore(state => state.annotationOriginal);
  const annotationDraft = useExplore(state => state.annotationDraft);

  const samples = sections.flatMap(v => v);

  const enabledInfoAlert = useMemo(() => {
    if (annotationDraft?.annotation3d && annotationOriginal?.annotation3d) {
      return getMeasurementDifferences(
        annotationDraft?.annotation3d,
        annotationOriginal?.annotation3d,
      );
    } else {
      return false;
    }
  }, [annotationDraft?.annotation3d, annotationOriginal?.annotation3d]);

  // Compute x-axis data
  const xData = useMemo(() => {
    const lengths = [0];
    for (let i = 1; i < samples.length; i++) {
      const a = samples[i - 1].clone();
      const b = samples[i].clone();

      a.height = 0;
      b.height = 0;

      const cartesianA = toCartesian(a);
      const cartesianB = toCartesian(b);

      lengths.push(lengths[i - 1] + cartesianA.distanceTo(cartesianB));
    }
    return lengths;
  }, [samples]);

  // Compute y-axis data
  const yData = useMemo(() => samples.map(v => v.height), [samples]);

  const exportCSV = () => {
    if (!sections) return;
    const infoToast = toast({
      type: 'info',
      message: `Exporting section surface profile CSV.`,
    });

    let csv = 'Segment,Longitude,Latitude,Height\n';
    for (const [i, line] of sections.entries()) {
      for (const section of line) {
        const longitude = toDegrees(section.longitude);
        const latitude = toDegrees(section.latitude);
        csv += `(P${i + 1} - P${i + 2}),${longitude},${latitude},${section.height}\n`;
      }
    }

    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `surface-profile.csv`;
    a.click();
    toast({
      type: 'success',
      message: `Successfully exported section surface profile CSV.`,
      lifespan: 5000,
    });
    infoToast.dismiss();
  };

  return (
    <div>
      <div
        className={cn(
          'mt-4',
          'items-center',
          'justify-center',
          'border-[1px]',
          'border-neutral-300',
          'rounded-md',
          'border-solid',
          'bg-neutral-200',
          'p-2',
        )}
      >
        {sections && (
          <LineChart
            height={200}
            series={[{ data: yData, showMark: false }]}
            xAxis={[{ data: xData, scaleType: 'linear', label: 'Distance along line (m)' }]}
            yAxis={[{ label: 'Elevation (m)' }]}
          />
        )}
      </div>
      <Button className="mt-3 w-full" onClick={exportCSV} primary size="small">
        Export to CSV
      </Button>
      {enabledInfoAlert && (
        <div
          className={cn(
            'w-full',
            'mt-3',
            'p-2',
            'mb-3',
            'bg-info-100',
            'border',
            'border-info-400',
            'border-solid',
            'rounded',
            'flex',
            'flex-row',
            'pointer-events-none',
          )}
        >
          <div className="flex items-center gap-[14px]">
            <div className="i-skand-info text-[12px] color-info-400" />
            <div>
              <p className="color-neutral-800 typo-text-s">
                Want to keep the measurement reference? Just click
              </p>
              <p className="color-neutral-800 typo-text-s-em">Update.</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
